/**
 * useSlidePreviews — fetches WYSIWYG per-slide PNG thumbnails for a
 * sandbox-authored presentation ``document_create`` proposal.
 *
 * A sandbox deck (``transform_file_with_code``) ships a FINISHED .pptx binary
 * (no structured ``slides[]`` outline), so it has no ``slide_list`` schema and
 * ``useSlideOutlineDraft().hasSlideList`` stays false — the confirm card would
 * fall back to a text summary. This hook renders the ACTUAL deck (BE:
 * ``GET /actions/{id}/slide-previews`` → LibreOffice→PDF→PyMuPDF, cached in
 * Redis) so the card can show the real slides before Confirm.
 *
 * Enable ONLY for sandbox-authored presentations (the caller knows
 * ``documentType === "presentation" && sandboxAuthored``) — every other
 * proposal returns an empty list from the BE anyway, so gating avoids a
 * pointless render request. Sibling to ``useSlideOutlineDraft`` /
 * ``useDocumentCreateDestination``.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getSlidePreviews, type SlidePreviewsResponse } from "@ando/core";

export interface SlidePreviewImage {
  readonly index: number;
  /** ``data:image/png;base64,…`` URI. */
  readonly url: string;
}

export interface UseSlidePreviewsResult {
  readonly query: UseQueryResult<SlidePreviewsResponse>;
  /** Ordered thumbnails; empty until the render lands or when disabled. */
  readonly slides: ReadonlyArray<SlidePreviewImage>;
  readonly isLoading: boolean;
}

export const slidePreviewsQueryKey = (proposalId: string | null | undefined) =>
  ["slide-previews", proposalId ?? ""] as const;

export function useSlidePreviews(
  proposalId: string | null | undefined,
  enabled: boolean,
): UseSlidePreviewsResult {
  const query = useQuery<SlidePreviewsResponse>({
    queryKey: slidePreviewsQueryKey(proposalId),
    queryFn: () => getSlidePreviews(proposalId ?? ""),
    enabled: Boolean(proposalId) && enabled,
    // Thumbnails are immutable for a given proposal (the deck bytes are
    // frozen at proposal time) — no need to refetch within a session.
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });

  const slides: ReadonlyArray<SlidePreviewImage> = (query.data?.slides ?? []).map(
    (s) => ({ index: s.index, url: s.image }),
  );

  return { query, slides, isLoading: query.isLoading && Boolean(enabled) };
}
